import type { ReactNode } from 'react';
import { AppRouting } from '@/routing/app-routing';
import { OidcAuthProvider, type OidcConfig } from '@tagna/auth';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { HelmetProvider } from '@/lib/helmet';
import { BrowserRouter } from 'react-router-dom';
import { LoadingBarContainer } from 'react-top-loading-bar';
import { Toaster } from '@/components/ui/sonner';
import { DEFAULT_AUTHORITY, loadOidcConfig } from './config/loadOidcConfig';
import { AuthTokenProvider } from './providers/auth-token-provider';
import { I18nProvider } from './providers/i18n-provider';
import { ModulesProvider } from './providers/modules-provider';
import { QueryProvider } from './providers/query-provider';
import { SettingsProvider } from './providers/settings-provider';
import { ThemeProvider } from './providers/theme-provider';
import { TooltipsProvider } from './providers/tooltips-provider';

const { BASE_URL } = import.meta.env;

function AppContent({ config }: { config: OidcConfig }) {
  return (
    <OidcAuthProvider config={config}>
      <AuthTokenProvider>
        <SettingsProvider>
          <ThemeProvider>
            <I18nProvider>
              <HelmetProvider>
                <TooltipsProvider>
                  <LoadingBarContainer>
                    <BrowserRouter basename={BASE_URL}>                        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 z-50 bg-white px-4 py-2 rounded shadow-lg text-primary font-medium">
                          Skip to main content
                        </a>                      <Toaster />
                      <ModulesProvider>
                        <AppRouting />
                      </ModulesProvider>
                    </BrowserRouter>
                  </LoadingBarContainer>
                </TooltipsProvider>
              </HelmetProvider>
            </I18nProvider>
          </ThemeProvider>
        </SettingsProvider>
      </AuthTokenProvider>
    </OidcAuthProvider>
  );
}

function FullscreenState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
      <div className="max-w-md space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
          <p className="mt-2 text-sm text-gray-600">{message}</p>
        </div>
        {action}
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
      <div className="max-w-md space-y-6">
        <div className="flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Connecting to Tagna Identity…
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Fetching discovery metadata to configure single sign-on.
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({
  onRetry,
  authority,
  errorMessage,
}: {
  onRetry: () => void;
  authority: string;
  errorMessage: string;
}) {
  return (
    <FullscreenState
      title="Unable to reach Tagna Identity"
      message={
        `We couldn't load the discovery document from ${authority}. ` +
        'Ensure the identity service is running and its development certificate is trusted, then retry.'
      }
      action={
        <div className="space-y-3">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-primary text-white font-medium hover:bg-primary/90"
          >
            Retry connection
          </button>
          <p className="text-xs text-gray-400">Error details: {errorMessage}</p>
        </div>
      }
    />
  );
}

function OidcConfigGate() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['oidc', 'config'],
    queryFn: loadOidcConfig,
    staleTime: Infinity,
    retry: false,
  });

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!data || error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    const authority = data?.authority ?? DEFAULT_AUTHORITY;
    return (
      <ErrorScreen
        onRetry={() => refetch()}
        authority={authority}
        errorMessage={message}
      />
    );
  }

  return <AppContent config={data.config} />;
}

export function App() {
  return (
    <QueryProvider>
      <OidcConfigGate />
    </QueryProvider>
  );
}
